import { useEffect, useRef, useState } from 'react';
import { Plus, X, Search, Trash2, Check, AlertCircle, ChevronLeft, ChevronRight, ChevronsLeft, ChevronUp, ChevronsRight, ChevronDown, Eye, EyeOff, RefreshCw, Shield, FileText, Building, Edit, Folder, Columns3, Loader2 } from 'lucide-react';
import { hasPermission } from '../utils/permissionsHelper';
import { jwt_decode } from '../utils/jwt_decode';
import baseapi from '../services/baseapi';
import { showError, showSuccess } from '../utils/toast';
import { CustomDropdown } from '../components/CustomDropdown ';
import { Modal } from '@mui/material';
import RolePermissionsSidebar from '../components/RolePermissionsSidebar';
import { useClientLevel } from '../hooks/useClientLevel';

// Updated interface to match new API structure
interface Role {
    id: string;
    client_id: string;
    composite: boolean;
    client_name: string;
    description: string;
    is_deleted: boolean;
    is_system_role: any;
    organization_id: string;
    permissions: Record<string, string>; // e.g., {"system": "crud", "user_management": "cr"}
    role: string;
}

interface Category {
    id: string;
    name: string;
    description: string;
    models: string[];
    model_count: number;
}

interface Operation {
    id: string;
    name: string;
    description: string;
}

const Roles = () => {
    const [showAddForm, setShowAddForm] = useState(false);
    const [showEditForm, setShowEditForm] = useState(false);
    const [editingRole, setEditingRole] = useState<Role | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [debouncedSearch, setDebouncedSearch] = useState('');
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [sortConfig, setSortConfig] = useState<{ key: string; direction: 'asc' | 'desc' } | null>(null);
    const [roles, setRoles] = useState<Role[]>([]);
    const [deleteRole, setDeleteRole] = useState<Role | null>(null);
    const [open, setOpen] = useState(false);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [editid, seteditid] = useState('');
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const decodedToken = jwt_decode();
    const organizationId = decodedToken?.organization_id;
    const [selectedRoleForPermissions, setSelectedRoleForPermissions] = useState<Role | null>(null);

    // Pagination states
    const [totalItems, setTotalItems] = useState(0);
    const [totalPages, setTotalPages] = useState(0);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [itemsPerPage, setItemsPerPage] = useState<number>(5);
    const startIndex = (currentPage - 1) * itemsPerPage;

    // Column visibility state
    const [showColumnFilter, setShowColumnFilter] = useState(false);
    type ColumnKey = 'role' | 'description' | 'actions' | 'type' | 'client_name';

    //permissions
    const canCreate = hasPermission('roles', 'create')
    const canDelete = hasPermission('roles', 'delete')
    const canUpdate = hasPermission('roles', 'update')

    const isFirstSearch = useRef(true);

    const [visibleColumns, setVisibleColumns] = useState<Record<ColumnKey, boolean>>({
        role: true,
        description: true,
        type: true,
        client_name: true,
        actions: canDelete || canUpdate
    });

    // 1. Read level from localStorage
    const level = useClientLevel();
    const [clients, setClients] = useState<{
        id: any; client_id: string; name: string; display_name: string
    }[]>([]);
    const [selectedClientId, setSelectedClientId] = useState('');

    // Fetch clients if global
    useEffect(() => {
        if (level?.name === 'global') {
            baseapi.get('/clients', { params: { limit: 1000, skip: 0 } })
                .then(res => {
                    if (res.data.status) {
                        setClients(res.data.data);
                    }
                })
                .catch(() => { });
        }
    }, [level]);

    useEffect(() => {
        fetchData(0, itemsPerPage);
    }, [level]);

    // Debounce search term
    useEffect(() => {
        const handler = setTimeout(() => {
            if (debouncedSearch !== searchTerm) {
                setDebouncedSearch(searchTerm);
                setCurrentPage(1);
            }
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm, debouncedSearch]);

    // Re-fetch when debouncedSearch changes
    useEffect(() => {
        if (isFirstSearch.current) {
            isFirstSearch.current = false;
            return;
        }
        fetchData(0, itemsPerPage);
    }, [debouncedSearch]);

    // Add state for fetched categories and operations
    const [rolePermissions, setRolePermissions] = useState<{
        categories: Category[];
        operations: Operation[]
    }>({
        categories: [],
        operations: []
    });
    const [loadingRolePermissions, setLoadingRolePermissions] = useState(false);

    // Updated formData to store category-wise operations
    const [formData, setFormData] = useState({
        role: '',
        description: '',
        categoryOperations: {} as Record<string, string[]> // e.g., {"system": ["create", "read"], "user_management": ["read"]}
    });

    const fetchData = async (skip: number = 0, limit: number = 5, orderBy: Record<string, 'asc' | 'desc'>[] = [{ created: 'desc' }]) => {
        try {
            setLoading(true);
            const [sortEntry] = orderBy;
            const sort_by = Object.keys(sortEntry)[0];
            const sort_order = sortEntry[sort_by];
            let params: any = {}
            const scope_level = level;
            const clientIds = localStorage.getItem('client_ids');
            const queryObj: any = {};

            if (scope_level.name !== 'global') {
                queryObj.client_id = scope_level.id ? Number(scope_level.id) : '';
            } else {
                if (!clientIds || clientIds.length === 0) {
                    queryObj.is_global = true;
                }
            }
            if (debouncedSearch) {
                queryObj.$or = [
                    { role: debouncedSearch },
                    { description: debouncedSearch }
                ];
            }
            params = {
                query: JSON.stringify(queryObj),
                skip: skip,
                limit: limit,
                sort_by,
                sort_order
            };
            const response = await baseapi.get("/roles", { params });
            if (response.data.status) {
                const newRoles = response.data.data;
                setRoles(newRoles);
                setTotalPages(Math.ceil(response.data.total / limit));
                setTotalItems(response.data.total);
                const maxPage = Math.ceil(response.data.total / limit) || 1;
                if (currentPage > maxPage) {
                    setCurrentPage(maxPage);
                }
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'raised an error in console';
            showError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (showAddForm || showEditForm) {
            setLoadingRolePermissions(true);
            baseapi.get('/roles/rolepermissions')
                .then(res => {
                    if (res.data.status) {
                        setRolePermissions({
                            categories: res.data.data.categories,
                            operations: res.data.data.operations
                        });
                    } else {
                        showError(res.data.message);
                    }
                })
                .catch(err => {
                    showError(err?.response?.data?.message || err.message || 'Failed to fetch role permissions');
                })
                .finally(() => setLoadingRolePermissions(false));
        }
    }, [showAddForm, showEditForm]);

    const handleSort = (key: string) => {
        setSortConfig((prev) => {
            if (prev?.key === key) {
                const newDirection = prev.direction === 'asc' ? 'desc' : 'asc';
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: newDirection }]);
                return { key, direction: newDirection };
            } else {
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage, [{ [key]: 'asc' }]);
                return { key, direction: 'asc' };
            }
        });
    };

    const validateForm = () => {
        const newErrors: Record<string, string> = {};
        if (!formData.role.trim()) newErrors.role = 'Role name is required';
        if (!formData.description.trim()) newErrors.description = 'Description is required';

        const selectedCategories = Object.keys(formData.categoryOperations);
        if (selectedCategories.length === 0) {
            newErrors.categories = 'At least one category with operations is required';
        } else {
            // Check if each selected category has at least one operation
            const hasEmptyCategory = selectedCategories.some(cat =>
                !formData.categoryOperations[cat] || formData.categoryOperations[cat].length === 0
            );
            if (hasEmptyCategory) {
                newErrors.operations = 'Each selected category must have at least one operation';
            }
        }

        // if (level?.name === 'global' && !selectedClientId) newErrors.client = 'Client is required';
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleInputChange = (field: string, value: string | Record<string, string[]>) => {
        setFormData(prev => ({ ...prev, [field]: value }));
        if (errors[field]) {
            setErrors(prev => ({ ...prev, [field]: '' }));
        }
    };

    // Helper function to convert operations array to permission string
    const convertOperationsToString = (operations: string[]): string => {
        const operationMap: Record<string, string> = {
            'create': 'c',
            'read': 'r',
            'update': 'u',
            'delete': 'd'
        };

        return operations
            .map(op => operationMap[op] || '')
            .filter(Boolean)
            .join('');
    };

    // Helper function to convert permission string to operations array
    const convertStringToOperations = (permissionStr: string): string[] => {
        const reverseMap: Record<string, string> = {
            'c': 'create',
            'r': 'read',
            'u': 'update',
            'd': 'delete'
        };

        return permissionStr.split('').map(char => reverseMap[char]).filter(Boolean);
    };

    const handleSubmit = async () => {
        if (!validateForm()) return;
        try {
            setSubmitting(true);
            const clientIdToUse = level?.name === 'global' ? selectedClientId : level?.id || '';

            // Convert categoryOperations to the required format
            const permissions: Record<string, string> = {};
            Object.keys(formData.categoryOperations).forEach(category => {
                permissions[category] = convertOperationsToString(formData.categoryOperations[category]);
            });

            const params = {
                role: formData.role,
                role_id: formData.role,
                ...(clientIdToUse
                    ? { client_id: Number(clientIdToUse) }
                    : { is_global: true }
                ),
                description: formData.description,
                permissions: permissions,
                is_system_role: false,
                organization_id: Number(organizationId)
            };

            const response = await baseapi.post('/roles', params);
            if (response.data.status) {
                showSuccess(response.data.message);
                resetForm();
                setShowAddForm(false);
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
                setErrors({});
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'Something went wrong';
            showError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    function getChangedFields(originalRole: Role, editForm: {
        role: string;
        description: string;
        client_id: string;
        categoryOperations: Record<string, string[]>;
    }): Partial<Role> {
        const changed: Partial<Role> = {};

        if (originalRole.role !== editForm.role) changed.role = editForm.role;
        if (originalRole.description !== editForm.description) changed.description = editForm.description;
        if (originalRole.client_id !== editForm.client_id) changed.client_id = editForm.client_id;

        const newPermissions: Record<string, string> = {};
        Object.keys(editForm.categoryOperations).forEach(cat => {
            newPermissions[cat] = convertOperationsToString(editForm.categoryOperations[cat]);
        });

        // Parse original permissions securely
        const originalPermissions = parsePermissions(originalRole.permissions);

        const permissionsChanged = (
            Object.keys(newPermissions).length !== Object.keys(originalPermissions).length
            || Object.keys(newPermissions).some(cat => newPermissions[cat] !== originalPermissions[cat])
            || Object.keys(originalPermissions).some(cat => !(cat in newPermissions))
        );

        if (permissionsChanged) {
            changed.permissions = newPermissions;
        }

        return changed;
    }




    const handleEditSubmit = async () => {
        if (!validateForm() || !editingRole) return;
        try {
            setSubmitting(true);
            const clientIdToUse = level?.name === 'global' ? selectedClientId : level?.id || '';

            // Get diff object containing only what changed
            const diff = getChangedFields(editingRole, {
                role: formData.role,
                description: formData.description,
                client_id: clientIdToUse,
                categoryOperations: formData.categoryOperations
            });


            const response = await baseapi.put(`/roles/${editid}`, diff);
            if (response.data.status) {
                showSuccess(response.data.message || 'Role updated successfully');
                resetForm();
                setShowEditForm(false);
                setEditingRole(null);
                fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
                setErrors({});
                seteditid('');
            } else {
                showError(response.data.message);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'Something went wrong';
            showError(errorMsg);
        } finally {
            setSubmitting(false);
        }
    };

    // Helper function to safely parse permissions
    const parsePermissions = (permissions: Record<string, string> | string | null | undefined): Record<string, string> => {
        if (!permissions) return {};
        if (typeof permissions === 'string') {
            try {
                return JSON.parse(permissions);
            } catch {
                return {};
            }
        }
        return typeof permissions === 'object' ? permissions : {};
    };


    // Updated handleEdit to parse the new permissions format
    const handleEdit = (role: Role) => {
        setEditingRole(role);

        // Safely parse permissions
        const parsedPermissions = parsePermissions(role.permissions);

        // Convert permissions object to categoryOperations format
        const categoryOperations: Record<string, string[]> = {};
        Object.keys(parsedPermissions).forEach(category => {
            categoryOperations[category] = convertStringToOperations(parsedPermissions[category]);
        });

        setFormData({
            role: role.role,
            description: role.description || '',
            categoryOperations: categoryOperations
        });
        seteditid(role.id);
        setSelectedClientId(role.client_id);
        setShowEditForm(true);
    };


    const resetForm = () => {
        setFormData({
            role: '',
            description: '',
            categoryOperations: {}
        });
        setShowAddForm(false);
        setShowEditForm(false);
        setEditingRole(null);
        setErrors({});
        setSelectedClientId('');
    };

    // Updated handleDelete
    const handleDelete = async (_roleId: string, id: string) => {
        try {
            setLoading(true);

            // Optimistically update roles
            const updatedRoles = roles.filter(role => role.id !== id);
            setRoles(updatedRoles);

            const response = await baseapi.delete(`/roles/${id}`);

            if (response.data.status) {
                showSuccess("Role Deleted Successfully");
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            } else {
                showError(response.data.message || 'Failed to delete role');
                await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
            }
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || error.message || 'An error occurred';
            showError(errorMsg);
            await fetchData((currentPage - 1) * itemsPerPage, itemsPerPage);
        } finally {
            setLoading(false);
        }
    };

    const handleRefresh = () => {
        setIsRefreshing(true);
        setCurrentPage(1);
        setSearchTerm('');
        setDebouncedSearch('');
        fetchData();
        setTimeout(() => {
            setIsRefreshing(false);
        }, 1000);
    };

    // Pagination functions
    const handlePageChange = (page: number): void => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
            fetchData((itemsPerPage * (page - 1)), itemsPerPage);
        }
    };

    const handleItemsPerPageChange = (value: number): void => {
        setItemsPerPage(value);
        setCurrentPage(1);
        fetchData(0, value);
    };

    // Column visibility functions
    const handleColumnToggle = (column: ColumnKey) => {
        setVisibleColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    };

    const columnOptions: { key: ColumnKey; label: string }[] = [
        { key: 'role', label: 'Role' },
        { key: 'description', label: 'Description' },
        { key: 'type', label: 'Type' },
        { key: 'client_name', label: 'Client Name' },
        ...((canDelete || canUpdate)
            ? [{ key: 'actions' as ColumnKey, label: 'Actions' }]
            : []),
    ];





    // Toggle category selection
    const toggleCategory = (categoryId: string) => {
        setFormData(prev => {
            const newCategoryOperations = { ...prev.categoryOperations };
            if (categoryId in newCategoryOperations) {
                delete newCategoryOperations[categoryId];
            } else {
                newCategoryOperations[categoryId] = [];
            }
            return { ...prev, categoryOperations: newCategoryOperations };
        });

        // Clear errors when category is toggled
        if (errors.categories || errors.operations) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.categories;
                delete newErrors.operations;
                return newErrors;
            });
        }
    };

    // Toggle operation for a specific category
    const toggleCategoryOperation = (categoryId: string, operationId: string) => {
        setFormData(prev => {
            const newCategoryOperations = { ...prev.categoryOperations };
            if (!newCategoryOperations[categoryId]) {
                newCategoryOperations[categoryId] = [operationId];
            } else {
                const operations = newCategoryOperations[categoryId];
                if (operations.includes(operationId)) {
                    newCategoryOperations[categoryId] = operations.filter(op => op !== operationId);
                } else {
                    newCategoryOperations[categoryId] = [...operations, operationId];
                }
            }
            return { ...prev, categoryOperations: newCategoryOperations };
        });

        // Clear operation error when any operation is toggled
        if (errors.operations) {
            setErrors(prev => {
                const newErrors = { ...prev };
                delete newErrors.operations;
                return newErrors;
            });
        }
    };

    const isCategorySelected = (categoryId: string) => {
        return categoryId in formData.categoryOperations;
    };

    const isCategoryOperationSelected = (categoryId: string, operationId: string) => {
        return formData.categoryOperations[categoryId]?.includes(operationId) || false;
    };




    const renderFormSidebar = (isEdit: boolean = false) => (
        <div
            className={`fixed top-10 pb-25 right-0 sm:right-4 h-[calc(100vh-2rem)] w-full max-w-full sm:max-w-[480px] md:max-w-[520px] lg:max-w-[580px] transition-all duration-500 ease-in-out transform z-51 ${(isEdit ? showEditForm : showAddForm) ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0 pointer-events-none'
                }`}
        >
            <div className="h-full bg-white shadow-2xl border border-slate-200 rounded-2xl flex flex-col">
                <div className="flex items-center justify-between px-4 py-3 sm:p-4 border-b border-slate-100 bg-slate-50/50 rounded-t-2xl">
                    <div className="flex items-center gap-3">
                        <div className="p-2 sm:p-3 bg-[#422568] rounded-xl">
                            {isEdit ? <Edit className="text-white" size={20} /> : <Plus className="text-white" size={20} />}
                        </div>
                        <div>
                            <h2 className="text-lg sm:text-xl font-black text-slate-800">
                                {isEdit ? 'Edit Role' : 'Add New Role'}
                            </h2>
                            <p className="text-slate-400 text-xs font-bold uppercase tracking-wider mt-1">
                                {isEdit ? 'Update the role details' : 'Fill in the details to create a new role'}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={resetForm}
                        className="p-2 sm:p-3 text-slate-400 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors active:scale-95"
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Form Content */}
                <div className="flex-1 overflow-y-auto p-4 sm:p-6 custom-scroll">
                    <div className="space-y-4">
                        {/* Role Name */}
                        <div>
                            <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                                <Shield size={18} className="text-[#422568]" />
                                Role Name <span className="text-red-500 text-base">*</span>
                            </label>
                            <input
                                type="text"
                                value={formData.role}
                                onChange={(e) => handleInputChange('role', e.target.value)}
                                placeholder="Enter role name..."
                                className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 text-sm font-semibold transition-all duration-300 ${errors.role
                                        ? 'border-red-500 bg-red-50/50 text-red-900'
                                        : 'border-slate-100 focus:border-[#422568] focus:bg-white focus:ring-4 focus:ring-[#422568]/10 outline-none'
                                    }`}
                            />
                            {errors.role && (
                                <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                    <AlertCircle size={12} />
                                    {errors.role}
                                </div>
                            )}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                                <FileText size={18} className="text-[#422568]" />
                                Description <span className="text-red-500 text-base">*</span>
                            </label>
                            <textarea
                                value={formData.description}
                                onChange={(e) => handleInputChange('description', e.target.value)}
                                placeholder="Enter role description..."
                                className={`w-full px-4 py-3 bg-slate-50 border-2 rounded-xl text-slate-800 placeholder-slate-400 text-sm font-semibold transition-all duration-300 ${errors.description
                                        ? 'border-red-500 bg-red-50/50 text-red-900'
                                        : 'border-slate-100 focus:border-[#422568] focus:bg-white focus:ring-4 focus:ring-[#422568]/10 outline-none'
                                    }`}
                                rows={2}
                            />
                            {errors.description && (
                                <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                    <AlertCircle size={12} />
                                    {errors.description}
                                </div>
                            )}
                        </div>

                        {/* Client Selection for Global Level */}
                        {level?.name === 'global' && (
                            <div>
                                <label className="block text-xs font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-2">
                                    <Building size={18} className="text-[#422568]" />
                                    Client
                                </label>
                                <CustomDropdown
                                    data={clients.map(client => ({
                                        label: client.display_name || client.name,
                                        value: client.id,
                                    }))}
                                    selectedValue={selectedClientId}
                                    onSelect={setSelectedClientId}
                                    placeholder="Select a client"
                                    className="w-full font-bold"
                                />
                                {!selectedClientId && errors.client && (
                                    <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                        <AlertCircle size={12} />
                                        {errors.client}
                                    </div>
                                )}
                            </div>
                        )}

                        {/* Categories with Operations */}
                        <div>
                            <div className="flex items-center justify-between mb-2">
                                <label className="text-xs font-black uppercase tracking-widest text-slate-400 flex items-center gap-2">
                                    <Folder size={18} className="text-[#422568]" />
                                    Categories & Operations <span className="text-red-500 text-base">*</span>
                                </label>
                                {!loadingRolePermissions && rolePermissions.categories.length > 0 && (
                                    <div
                                        className="flex items-center gap-2 px-2.5 py-1 rounded bg-slate-50 border border-slate-200 hover:bg-slate-100 transition-colors cursor-pointer"
                                        onClick={() => {
                                            const isAllSelected = rolePermissions.categories.length > 0 && rolePermissions.categories.every(cat =>
                                                (formData.categoryOperations[cat.id] || []).length === rolePermissions.operations.length
                                            );
                                            const newCategoryOperations: Record<string, string[]> = {};
                                            if (!isAllSelected) {
                                                const allOps = rolePermissions.operations.map(op => op.id);
                                                rolePermissions.categories.forEach(cat => {
                                                    newCategoryOperations[cat.id] = allOps;
                                                });
                                            }
                                            handleInputChange('categoryOperations', newCategoryOperations);
                                            setErrors(prev => {
                                                const newErrors = { ...prev };
                                                delete newErrors.categories;
                                                delete newErrors.operations;
                                                return newErrors;
                                            });
                                        }}
                                    >
                                        <input
                                            type="checkbox"
                                            checked={
                                                rolePermissions.categories.length > 0 &&
                                                rolePermissions.categories.every(cat =>
                                                    (formData.categoryOperations[cat.id] || []).length === rolePermissions.operations.length
                                                )
                                            }
                                            readOnly
                                            className="form-checkbox h-3.5 w-3.5 text-[#422568] rounded focus:ring-[#422568] border-slate-350 border-slate-350 border-slate-300 bg-white pointer-events-none"
                                        />
                                        <span className="text-[10px] font-bold text-slate-400 uppercase">Select All</span>
                                    </div>
                                )}
                            </div>

                            {loadingRolePermissions ? (
                                <div className="text-center text-slate-400 py-4 font-bold uppercase tracking-widest text-[10px] italic">Loading Permissions...</div>
                            ) : (
                                <div className="space-y-3">
                                    {rolePermissions.categories.map(category => {
                                        const isSelected = isCategorySelected(category.id);
                                        return (
                                            <div
                                                key={category.id}
                                                className={`border rounded-xl transition-all duration-200 ${isSelected
                                                    ? 'border-[#422568] bg-[#422568]/20 shadow-md shadow-[#422568]/30'
                                                    : 'border-slate-200 bg-slate-50/30'
                                                    }`}
                                            >
                                                {/* Category Header */}
                                                <div
                                                    className="px-4 py-3 cursor-pointer flex items-center justify-between"
                                                    onClick={() => toggleCategory(category.id)}
                                                >
                                                    <div className="flex items-center gap-3">
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={() => toggleCategory(category.id)}
                                                            className="form-checkbox h-4 w-4 text-[#422568] rounded focus:ring-[#422568] border-slate-300 bg-white pointer-events-none"
                                                        />
                                                        <div>
                                                            <div className="text-slate-800 font-extrabold text-sm capitalize">
                                                                {category.name.replace(/_/g, ' ')}
                                                            </div>
                                                            <div className="text-[11px] text-slate-400 font-semibold">{category.description}</div>
                                                        </div>
                                                    </div>
                                                    {isSelected && (
                                                        <ChevronDown className="w-4 h-4 text-slate-400" />
                                                    )}
                                                </div>

                                                {/* Operations Grid (shown when category is selected) */}
                                                {isSelected && (
                                                    <div className="px-4 pb-4 pt-2 border-t border-slate-100 bg-white rounded-b-xl">
                                                        <div className="text-[10px] text-slate-400 mb-2.5 font-black uppercase tracking-wider">Select Operations:</div>
                                                        <div className="flex items-center mb-3">
                                                            <input
                                                                type="checkbox"
                                                                checked={
                                                                    (formData.categoryOperations[category.id] || []).length === rolePermissions.operations.length
                                                                }
                                                                onChange={() => {
                                                                    const allOps = rolePermissions.operations.map((op) => op.id);
                                                                    setFormData((prev) => ({
                                                                        ...prev,
                                                                        categoryOperations: {
                                                                            ...prev.categoryOperations,
                                                                            [category.id]:
                                                                                (prev.categoryOperations[category.id] || []).length === allOps.length ? [] : allOps,
                                                                        },
                                                                    }));
                                                                }}
                                                                className="form-checkbox h-4 w-4 text-emerald-600 rounded border-slate-300 bg-white"
                                                            />
                                                            <span className="ml-2 text-xs text-slate-700 font-black uppercase tracking-wide">All Operations</span>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-2">
                                                            {rolePermissions.operations.map((operation) => {
                                                                const isOpSelected = isCategoryOperationSelected(category.id, operation.id);
                                                                return (
                                                                    <div
                                                                        key={operation.id}
                                                                        className={`p-2 rounded-xl border-2 cursor-pointer transition-all text-xs font-semibold ${isOpSelected
                                                                            ? 'border-emerald-500 bg-emerald-50/30 text-emerald-700 font-black shadow-sm'
                                                                            : 'border-slate-100 bg-slate-50/50 text-slate-600 hover:border-slate-200 hover:bg-white'
                                                                            }`}
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            toggleCategoryOperation(category.id, operation.id);
                                                                        }}
                                                                    >
                                                                        <div className="flex items-center gap-2">
                                                                            <input
                                                                                type="checkbox"
                                                                                checked={isOpSelected}
                                                                                onChange={() => toggleCategoryOperation(category.id, operation.id)}
                                                                                className="form-checkbox h-3 w-3 text-emerald-500 rounded border-slate-300 bg-white pointer-events-none"
                                                                            />
                                                                            <span className="capitalize">{operation.name}</span>
                                                                        </div>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {errors.categories && (
                                <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                    <AlertCircle size={12} />
                                    {errors.categories}
                                </div>
                            )}
                            {errors.operations && (
                                <div className="flex items-center gap-1.5 mt-1.5 text-red-500 text-[10px] font-black uppercase tracking-tight">
                                    <AlertCircle size={12} />
                                    {errors.operations}
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Form Footer */}
                <div className="p-4 sm:p-6 border-t border-slate-100 bg-slate-50/30">
                    <div className="flex flex-col sm:flex-row gap-3">
                        <button
                            onClick={isEdit ? handleEditSubmit : handleSubmit}
                            disabled={submitting}
                            className={`w-full sm:flex-1 flex items-center justify-center gap-3 bg-[#422568] text-white py-3 px-4 rounded-xl font-bold hover:bg-[#422568]/90 transform hover:scale-102 active:scale-98 transition-all duration-200 shadow-md hover:shadow-lg shadow-[#422568]/20 text-xs font-black uppercase tracking-wider ${submitting ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                            {submitting ? (
                                <RefreshCw className="w-5 h-5 animate-spin" />
                            ) : (
                                <Check size={18} />
                            )}
                            {submitting ? 'Submitting...' : (isEdit ? 'Update Role' : 'Create Role')}
                        </button>
                        <button
                            onClick={resetForm}
                            className="w-full sm:w-auto sm:px-6 py-3 bg-slate-100 text-slate-600 rounded-xl hover:bg-slate-200 transition-colors duration-200 font-bold text-xs font-black uppercase tracking-wider"
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="bg-slate-50 p-3 pb-20 min-h-screen">
            <div className="w-full mx-auto">
                {/* Top Header Row */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
                    <div className="flex items-center gap-1.5">
                        <div className="bg-[#422568] p-1.5 rounded shadow-lg">
                            <Shield className="w-4 h-4 text-white" />
                        </div>
                        <h1 className="font-black text-lg text-slate-800 leading-none flex items-center gap-1.5">
                            Roles
                            {!loading && (
                                <span className="text-slate-600 font-bold text-md">
                                    ({totalItems})
                                </span>
                            )}
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={handleRefresh}
                            className="rounded-lg bg-white border border-slate-200 text-slate-400 p-2 hover:bg-slate-50 hover:text-slate-800 transition-all shadow-sm active:scale-95"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${isRefreshing ? 'animate-spin' : ''}`} />
                        </button>

                        <div className="relative w-full sm:w-56">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search roles..."
                                className="w-full bg-white border border-slate-200 rounded-lg pl-8 pr-3 py-1.5 text-[10px] text-slate-800 focus:border-[#422568] focus:ring-4 focus:ring-[#422568]/5 outline-none transition-all font-bold placeholder:text-slate-400 shadow-sm"
                                value={searchTerm}
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setCurrentPage(1);
                                }}
                            />
                        </div>

                        <div className="relative" data-col-menu>
                            <button
                                onClick={() => setShowColumnFilter(!showColumnFilter)}
                                className="p-2 bg-white border border-slate-200 rounded-lg hover:bg-slate-50 hover:text-slate-800 text-slate-600 transition-all flex items-center gap-2 shadow-sm"
                            >
                                <Columns3 className="w-3.5 h-3.5" />
                                <span className="text-[10px] font-bold uppercase tracking-wider">Columns</span>
                            </button>
                            {showColumnFilter && (
                                <div className="absolute top-full mt-2 right-0 w-52 bg-white border border-slate-200 rounded-xl shadow-2xl z-[110] p-1.5 max-h-[450px] overflow-y-auto origin-top animate-in fade-in-0 zoom-in-95 duration-100">
                                    <div className="px-3 py-1.5 text-[9px] font-bold text-slate-400 uppercase border-b border-slate-100 mb-1">Toggle Columns</div>
                                    {columnOptions.map((option) => (
                                        <button
                                            key={option.key}
                                            onClick={() => handleColumnToggle(option.key)}
                                            className="w-full flex items-center justify-between px-3 py-2 text-[10px] font-bold uppercase text-slate-600 hover:bg-slate-50 rounded-lg transition-colors"
                                        >
                                            <div className="flex items-center gap-2.5">
                                                {visibleColumns[option.key] ? (
                                                    <Eye className="w-3 h-3 text-[#422568]" />
                                                ) : (
                                                    <EyeOff className="w-3 h-3 text-slate-400" />
                                                )}
                                                {option.label}
                                            </div>
                                            <div className={`w-3 h-3 rounded border flex items-center justify-center transition-colors ${visibleColumns[option.key] ? 'bg-[#422568] border-[#422568]' : 'border-slate-300 bg-white'}`}>
                                                {visibleColumns[option.key] && <Check className="w-2.5 h-2.5 text-white" />}
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>

                        {canCreate && (
                            <button
                                onClick={() => {
                                    if (showAddForm) {
                                        resetForm();
                                    } else {
                                        setShowAddForm(true);
                                    }
                                }}
                                className="flex items-center gap-1.5 bg-[#422568] rounded-lg px-4 py-1.5 text-[10px] text-white font-bold hover:bg-[#422568]/80 transition-all"
                            >
                                <Plus className="w-4 h-4" />
                                Add Role
                            </button>
                        )}
                    </div>
                </div>

                {/* Table */}
                <div className="bg-white border border-slate-200 rounded-t-xl overflow-hidden shadow-xl shadow-slate-200/50 mt-4">
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-50/80 sticky top-0 z-10 backdrop-blur-sm">
                                <tr className="border-b border-slate-100">
                                    {columnOptions.map(({ key, label }) => (
                                        visibleColumns[key] && (
                                            <th
                                                key={key}
                                                onClick={() => key !== 'actions' && handleSort(key)}
                                                className={`px-3 py-2 text-[9px] font-black text-slate-400 uppercase tracking-widest select-none whitespace-nowrap cursor-pointer hover:bg-slate-100 transition-colors ${key === 'actions' ? 'text-right' : 'text-left'}`}
                                            >
                                                <div className={`flex items-center gap-1 ${key === 'actions' ? 'justify-end' : ''}`}>
                                                    {label}
                                                    {key !== 'actions' && (
                                                        sortConfig?.key === key ? (
                                                            sortConfig.direction === 'asc' ? (
                                                                <ChevronUp size={12} className="text-[#422568]" />
                                                            ) : (
                                                                <ChevronDown size={12} className="text-[#422568]" />
                                                            )
                                                        ) : (
                                                            <ChevronDown size={12} className="text-slate-300" />
                                                        )
                                                    )}
                                                </div>
                                            </th>
                                        )
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {(loading || isRefreshing) ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-slate-400 font-bold uppercase tracking-widest italic animate-pulse">
                                            <div className="flex items-center justify-center gap-2">
                                                <Loader2 className="w-4 h-4 animate-spin text-[#422568]" />
                                                Loading roles
                                            </div>
                                        </td>
                                    </tr>
                                ) : roles.length === 0 ? (
                                    <tr>
                                        <td colSpan={Object.values(visibleColumns).filter(Boolean).length} className="py-16 text-center text-slate-300 font-bold uppercase tracking-widest italic bg-slate-50/20">
                                            <div className="flex flex-col items-center gap-2">
                                                <Shield className="w-8 h-8 text-slate-100" /> No roles found.
                                            </div>
                                        </td>
                                    </tr>
                                ) : (
                                    roles.map((role) => (
                                        <tr key={role.id} className="hover:bg-slate-50 transition-colors group">
                                            {visibleColumns.role && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <div className="flex items-center gap-2">
                                                        <div className="w-6 h-6 rounded-md bg-slate-100 flex items-center justify-center text-[10px] font-black text-slate-500 group-hover:bg-[#422568] group-hover:text-[#422568] transition-colors shadow-sm">
                                                            {role.role.charAt(0)}
                                                        </div>
                                                        <button
                                                            onClick={() => setSelectedRoleForPermissions(role)}
                                                            className="text-[10px] text-[#422568] hover:text-[#422568] font-bold font-mono transition-colors hover:underline hover:cursor-pointer decoration-[#422568]/30 underline-offset-4"
                                                        >
                                                            {role.role}
                                                        </button>
                                                    </div>
                                                </td>
                                            )}
                                            {visibleColumns.description && (
                                                <td className="px-3 py-2 max-w-[250px] truncate">
                                                    <span className="text-slate-500 text-[10px] font-bold leading-tight">
                                                        {role.description}
                                                    </span>
                                                </td>
                                            )}

                                            {visibleColumns.type && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    {role.is_system_role ? (
                                                        <span className="text-[9px] px-2 py-0.5 rounded-md font-black uppercase tracking-wide shadow-sm text-green-600 bg-green-50 border border-green-100">
                                                            System
                                                        </span>
                                                    ) : (
                                                        <span className="text-[9px] px-2 py-0.5 rounded-md font-black uppercase tracking-wide shadow-sm text-sky-600 bg-sky-50 border border-sky-100">
                                                            Custom
                                                        </span>
                                                    )}
                                                </td>
                                            )}
                                            {visibleColumns.client_name && (
                                                <td className="px-3 py-2 whitespace-nowrap">
                                                    <span className="text-slate-500 text-[10px] font-mono font-bold flex items-center gap-1.5 bg-slate-50 px-2 py-0.5 rounded border border-slate-100 w-fit">
                                                        {role.client_name || '-'}
                                                    </span>
                                                </td>
                                            )}
                                            {visibleColumns.actions && (
                                                <td className="px-3 py-2 whitespace-nowrap text-right">
                                                    <div className="flex items-center justify-end space-x-1.5">
                                                        {canUpdate && (
                                                            <button
                                                                onClick={() => handleEdit(role)}
                                                                className="text-slate-400 hover:text-slate-800 p-1.5 rounded-lg hover:bg-slate-100 transition-all active:scale-90"
                                                            >
                                                                <Edit className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                        {canDelete && (
                                                            <button
                                                                onClick={() => {
                                                                    setDeleteRole(role);
                                                                    setOpen(true);
                                                                }}
                                                                className="text-rose-500 hover:text-rose-700 p-1.5 rounded-lg hover:bg-rose-50 transition-all active:scale-90"
                                                            >
                                                                <Trash2 className="w-3.5 h-3.5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            )}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>

                {/* Table Footer */}
                <div className="bg-slate-50 border-t border-slate-200 px-6 py-2.5 flex items-center justify-between rounded-b-xl shadow-sm">
                    <div className="flex items-center gap-4">
                        <select
                            className="px-2 py-1 bg-white border border-slate-200 rounded text-slate-800 text-[10px] focus:outline-none focus:ring-1 focus:ring-[#422568] font-bold shadow-sm"
                            value={itemsPerPage}
                            onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={50}>50</option>
                        </select>
                        <span className="text-[9px] text-slate-400 font-bold uppercase tracking-widest">
                            <span className="text-slate-800">{startIndex + 1}</span> - <span className="text-slate-800">{Math.min(startIndex + itemsPerPage, totalItems)}</span> OF {totalItems}
                        </span>
                    </div>
                    <div className="flex items-center gap-1">
                        <button onClick={() => handlePageChange(1)} disabled={currentPage === 1} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronsLeft className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronLeft className="w-3.5 h-3.5" /></button>
                        <div className="px-2.5 py-1 bg-[#422568] rounded-md text-white text-[10px] font-black mx-1.5">{currentPage}</div>
                        <button onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === totalPages} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronRight className="w-3.5 h-3.5" /></button>
                        <button onClick={() => handlePageChange(totalPages)} disabled={currentPage === totalPages} className="p-1.5 text-slate-300 hover:text-slate-800 disabled:opacity-30 transition-colors"><ChevronsRight className="w-3.5 h-3.5" /></button>
                    </div>
                </div>

                {/* Add Form */}
                {renderFormSidebar(false)}

                {/* Edit Form */}
                {renderFormSidebar(true)}

                {/* Background Overlay */}
                <div
                    className={`fixed inset-0 bg-slate-900/40 backdrop-blur-sm transition-opacity duration-300 z-50 ${(showAddForm || showEditForm) ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
                    onClick={resetForm}
                />

                {/* Role Permissions Sidebar */}
                <RolePermissionsSidebar
                    isOpen={!!selectedRoleForPermissions}
                    onClose={() => setSelectedRoleForPermissions(null)}
                    roleName={selectedRoleForPermissions?.role || ''}
                    permissions={selectedRoleForPermissions?.permissions}
                />
            </div>

            {/* Click outside to close column filter dropdown */}
            {showColumnFilter && (
                <div
                    className="fixed inset-0 z-30"
                    onClick={() => setShowColumnFilter(false)}
                />
            )}

            {/* Delete Confirmation Modal */}
            <Modal open={open} onClose={() => setOpen(false)}>
                <div className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 
                w-[95%] sm:w-[500px] md:w-[550px] max-w-2xl bg-white 
                border border-slate-200 shadow-2xl p-4 rounded-2xl text-slate-800
                relative overflow-hidden">

                    {/* Subtle top border */}
                    <div className="absolute top-0 left-0 right-0 h-px bg-[#422568] to-transparent"></div>

                    {/* Header */}
                    <div className="flex items-center mb-4">
                        <div className="flex items-center justify-center mr-2 text-rose-500">
                            <Trash2 className='h-7 w-7'></Trash2>
                        </div>
                        <div className='flex items-center justify-center'>
                            <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tight">Confirm Delete</h2>
                        </div>
                    </div>

                    {/* Content */}
                    <div className="mb-4 bg-slate-50 p-4 rounded-xl border border-slate-100">
                        <p className="text-slate-800 mb-3 text-lg font-bold">
                            Are you sure you want to delete role <span className="font-extrabold text-[#422568]">{deleteRole?.role}</span>?
                        </p>
                        <p className="text-slate-400 text-sm font-semibold">
                            This will permanently remove the role from your account.
                        </p>
                    </div>

                    {/* Buttons */}
                    <div className="flex gap-4 justify-end">
                        <button
                            onClick={() => setOpen(false)}
                            className="px-6 py-3 border border-slate-200 text-slate-500 rounded-lg
                      hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800
                      transition-all duration-200 font-bold min-w-[100px]"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={() => {
                                if (deleteRole) {
                                    handleDelete(deleteRole.role, deleteRole.id);
                                    setOpen(false);
                                }
                            }}
                            className="px-6 py-3 bg-[#422568] text-white rounded-lg font-black uppercase tracking-wider
                      hover:bg-[#422568] hover:shadow-lg hover:shadow-[#422568]
                      transition-all duration-200 min-w-[100px]"
                        >
                            Delete Role
                        </button>
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default Roles;
